import React, { SyntheticEvent, useState, useEffect } from 'react';
import axios from 'axios';
import './index.css';
import RegionesYcomunas from './regionesComunas';
import PropTypes from 'prop-types';
import swal from 'sweetalert';
import { prettifyRut, formatRut } from 'react-rut-formatter';
import termsConditions from './termsCondition';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { z } from 'zod';

type RegisterFormProps = {
  mode: string,
  closeRegisterOpenSignIn: React.MouseEventHandler,
  toggleSignUpVet: React.MouseEventHandler,
  toggleSignUpUser: React.MouseEventHandler,
  closeRegisterOpenService: React.MouseEventHandler,
  navigate: Function,
  closePopupRegister: Function
}

function redirect(id: string, props: RegisterFormProps) {
  props.navigate('/');
}

type RegisterData = {
  email: string,
  password: string,
  name: string,
  addressComuna: string,
  addressRegion: string,
  address: string,
  phone: string,
  role: number,
  rut: string,
  birthdate: string,
  termsConditionsAccepted: boolean,
  area: string,
  gender: string
}

// Define Zod schema for the form
const registerSchema = z.object({
  email: z.string().email('Correo electrónico inválido'),
  password: z.string().min(8, 'La contraseña debe tener al menos 8 caracteres').regex(/[A-Z]/, 'Debe contener una mayúscula').regex(/[a-z]/, 'Debe contener una minúscula').regex(/[0-9]/, 'Debe contener un número'),
  name: z.string().min(1, 'Nombre es requerido'),
  addressComuna: z.string().min(1, 'Comuna es requerida'),
  addressRegion: z.string().min(1, 'Región es requerida'),
  address: z.string().min(1, 'Dirección es requerida'),
  phone: z.string().regex(/^\+569\d{8}$/, 'Formato de teléfono inválido'),
  role: z.number(),
  rut: z.string().min(1, 'RUT es requerido'),
  birthdate: z.string().min(1, 'Fecha de nacimiento es requerida'),
  termsConditionsAccepted: z.boolean().refine(val => val, { message: 'Debes aceptar los términos y condiciones' }),
  area: z.string().optional(),
  gender: z.string().min(1, 'Género es requerido')
});

function RegisterForm(props: RegisterFormProps) {
  const [datos, setDatos] = useState<RegisterData>({
    email: '',
    password: '',
    name: '',
    addressComuna: '',
    addressRegion: '',
    address: '',
    phone: '',
    role: 1,
    rut: '',
    birthdate: '',
    termsConditionsAccepted: false,
    area: '',
    gender: 'MALE'
  });
  const [passwordConfirmation, setPasswordConfirmation] = useState<string>('');
  const [comunas, setComunas] = useState<Array<string>>([]);
  const [agree, setAgree] = useState<boolean>(false);
  const [rut, setRut] = useState<string>('');
  const [showTermsConditions, setShowTermsConditions] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const [phone, setPhone] = useState<string>('');

  useEffect(() => {
    if (props.mode === 'vet') {
      setDatos({
        ...datos,
        role: 2
      });
    }
  }, []);

  const validateFields = () => {
    setFieldErrors({});
    try {
      registerSchema.parse({ ...datos, termsConditionsAccepted: agree });
      if (datos.password !== passwordConfirmation) {
        setFieldErrors({ passwordConfirmation: 'Las contraseñas no coinciden' });
        return false;
      }
      return true;
    } catch (err: any) {
      if (err.errors) {
        const errors: { [key: string]: string } = {};
        err.errors.forEach((e: any) => {
          errors[e.path[0]] = e.message;
        });
        if (datos.password !== passwordConfirmation) {
          errors.passwordConfirmation = 'Las contraseñas no coinciden';
        }
        setFieldErrors(errors);
      }
      return false;
    }
  };

  function handleComunas(event: SyntheticEvent) {
    event.preventDefault();
    const currentRegion = (event.target as HTMLInputElement).value;
    setDatos({
      ...datos,
      addressRegion: currentRegion
    });
    Object.values(RegionesYcomunas)[0].forEach(region => {
      if (region.NombreRegion === currentRegion) {
        setComunas(region.comunas.sort());
      }
    });
  };

  function handleInputChange(e: SyntheticEvent) {
    const target = e.target as HTMLInputElement;
    if (target.name === 'rut') {
      setRut(prettifyRut(target.value));
      setDatos({
        ...datos,
        [target.name]: formatRut(target.value)
      });
    } else if (target.name === 'phone') {
      // Only prettify if the user is adding characters, not deleting
      const isDeleting = phone.length > target.value.length;
      if (isDeleting) {
        setPhone(target.value);
      } else {
        setPhone(prettifyPhoneNumber(target.value));
      }
      setDatos({
        ...datos,
        [target.name]: formatPhoneNumber(target.value)
      });
    } else {
      setDatos({
        ...datos,
        [target.name]: target.value
      });
    }
  }

  function prettifyPhoneNumber(phoneNumber: string) {
    // Only format if the user is adding, not deleting
    const formattedNumber = phoneNumber.replace(/\s/g, '');
    if (formattedNumber.length <= 4) return formattedNumber;
    let result = formattedNumber.slice(0, 4);
    if (formattedNumber.length > 4) {
      result += ' ' + formattedNumber.slice(4, 8);
    }
    if (formattedNumber.length > 8) {
      result += ' ' + formattedNumber.slice(8, 12);
    }
    return result;
  }

  function formatPhoneNumber(phoneNumber:string) {
    return phoneNumber.replace(/\s/g, '');
  }

  function submitRegisterForm(event: SyntheticEvent) {
    event.preventDefault();
    setLoading(true);
    setFieldErrors({});
    if (!validateFields()) {
      setLoading(false);
      return;
    }
    datos.termsConditionsAccepted = agree;
    if (datos.role === 2) {
      const token = JSON.parse(localStorage.getItem('token') || '');
      axios.post(process.env.REACT_APP_URL + '/create/vet', datos, { headers: { Authorization: `Bearer ${token}` } })
        .then((response) => {
          swal({
            title: 'Registro exitoso',
            text: 'Ingresa al mail ' + String(datos.email) + ' y valida tu cuenta para poder iniciar sesión',
            icon: 'success',
            buttons: { Aceptar: true },
            timer: 10000
          });
          props.closePopupRegister();
          redirect(response.data.newUser.id, props);
        })
        .catch((error) => {
          setAgree(false);
          swal({
            title: 'Error',
            text: String(error.response.data.message),
            icon: 'error',
            buttons: { Aceptar: true },
            timer: 2000
          });
        }
        );
    } else {
      axios.post(process.env.REACT_APP_URL + '/create/user', datos)
        .then((response) => {
          swal({
            title: 'Registro exitoso',
            text: 'Ingresa al mail ' + String(datos.email) + ' y valida tu cuenta para poder iniciar sesión',
            icon: 'success',
            buttons: { Aceptar: true },
            timer: 10000
          });
          props.closePopupRegister();
          redirect(response.data.newUser.id, props);
        })
        .catch((error) => {
          setAgree(false);
          swal({
            title: 'Error',
            text: String(error.response.data.message),
            icon: 'error',
            buttons: { Aceptar: true },
            timer: 2000
          });
        }
        )
        .finally(() => {
          setLoading(false); // Desactivar la pantalla de carga
        });
    }
  }
  if (loading) {
    return (<div className="loading-label"><CircularProgress /></div>); // Mostrar el indicador de carga mientras loading es true
  }

  return (
    <form className="registerForm" onSubmit={submitRegisterForm}>
      <ul className="nav nav-pills justify-content-center nav-fill">
        <button className='closeForm' onClick={props.toggleSignUpUser}><CloseIcon /></button>
      </ul>
      <>
        <div className='register-title-div'>
          <h1 className='register-title'>¡Regístrate!</h1>
        </div>
        <div className='register-subtitle-div'>
          <p>Un RUT solo puede estar conectado a una cuenta</p>
        </div>
        <div className='fieldsets'>
            <fieldset className='fieldset-div'>
              <p>
                <input id="name" placeholder=" " type="text" value={datos.name} name="name" onChange={(e) => handleInputChange(e)} className="register-form-input" required />
                <label htmlFor="name">Nombre Completo</label>
                {fieldErrors.name && <span className="formError">{fieldErrors.name}</span>}
              </p>
            </fieldset>
            <fieldset className='fieldset-div'>
              <p>
                <input id="phone" placeholder=" " type="text" name="phone" className="register-form-input" value={phone} onChange={(e) => handleInputChange(e)} required />
                <label htmlFor="phone">Teléfono (+56 y 9 dígitos)</label>
                {fieldErrors.phone && <span className="formError">{fieldErrors.phone}</span>}
              </p>
            </fieldset>
            <fieldset className='fieldset-div'>
              <p>
                <select placeholder=" " id="addressRegion" name="addressRegion" className="register-selector form-select" value={datos.addressRegion} onChange={e => handleComunas(e)} required>
                  <option value="" defaultValue="" disabled hidden></option>
                  {Object.values(RegionesYcomunas)[0].map((region) => {
                    return <option key={region.NombreRegion}>{region.NombreRegion}</option>;
                  })}
                </select>
                <label htmlFor="addressRegion">Región</label>
                {fieldErrors.addressRegion && <span className="formError">{fieldErrors.addressRegion}</span>}
              </p>
            </fieldset>

            <fieldset className='fieldset-div'>
              <p>
                <select
                  id="addressComuna"
                  name="addressComuna"
                  className="register-selector form-select"
                  value={datos.addressComuna}
                  onChange={(e) => handleInputChange(e)}
                  required
                >
                  <option value="" defaultValue="" disabled hidden></option>
                  {comunas.map((comuna) => (<option key={comuna}>{comuna}</option>))}
                </select>
                <label htmlFor="addressComuna">Comuna</label>
                {fieldErrors.addressComuna && <span className="formError">{fieldErrors.addressComuna}</span>}
              </p>
            </fieldset>
            <fieldset className='fieldset-div'>
              <p>
                <input id="address" placeholder=" " type="address" name="address" value={datos.address} onChange={(e) => handleInputChange(e)} className="register-form-input" required />
                <label htmlFor="address">Dirección</label>
                {fieldErrors.address && <span className="formError">{fieldErrors.address}</span>}
              </p>
            </fieldset>
            <fieldset className='fieldset-div'>
              <p>
                <input id="email" placeholder=" " type="email" name="email" value={datos.email} onChange={(e) => handleInputChange(e)} className="register-form-input" required />
                <label htmlFor="email">Correo Electrónico</label>
                {fieldErrors.email && <span className="formError">{fieldErrors.email}</span>}
              </p>
            </fieldset>
            <fieldset className='fieldset-div'>
                <p>
                    <input placeholder=" " value={rut} id="rut" name="rut" onChange={(e) => handleInputChange(e)} className="register-form-input" required/>
                    <label htmlFor='rut'>RUT (con punto y guión)</label>
                    {fieldErrors.rut && <span className="formError">{fieldErrors.rut}</span>}
                </p>
            </fieldset>
            <fieldset className='fieldset-div'>
              <p>
                <input id="password" placeholder=" " type="password" value={datos.password} name='password' onChange={(e) => handleInputChange(e)} className="register-form-input" required />
                <label htmlFor='password'>Contraseña</label>
                <div className='password-requirements' > La contraseña debe contener al menos 8 caracteres, un número, una mayúscula y una minúscula.
                </div>
                {fieldErrors.password && <span className="formError">{fieldErrors.password}</span>}
              </p>
            </fieldset>
            <fieldset className='fieldset-div'>
              <p>
                <input placeholder=" " type="password" id="contraseñaConfirmation" name='passwordConfirmation' value={passwordConfirmation} onChange={e => setPasswordConfirmation(e.target.value)} className="register-form-input" required />
                <label htmlFor='contraseñaConfirmation'>Confirmar contraseña</label>
                {fieldErrors.passwordConfirmation && <span className="formError">{fieldErrors.passwordConfirmation}</span>}
              </p>
            </fieldset>
            <fieldset className='fieldset-div'>
              <p>
                <input placeholder=" " type="date" id="birthdate" name='birthdate' value={datos.birthdate} onChange={(e) => handleInputChange(e)} className="register-form-input" required />
                <label htmlFor='contraseñaConfirmation'>Fecha de Nacimiento</label>
                {fieldErrors.birthdate && <span className="formError">{fieldErrors.birthdate}</span>}
              </p>
            </fieldset>
            <fieldset className='fieldset-div'>
              <p>
                <select
                  id="gender"
                  name="gender"
                  className="register-selector form-select"
                  value={datos.gender}
                  onChange={(e) => handleInputChange(e)}
                  required
                >
                  <option value="MALE">Masculino</option>
                  <option value="FEMALE">Femenino</option>
                  <option value="OTHER">Otro</option>
                </select>
                <label htmlFor="addressComuna">Género</label>
                {fieldErrors.gender && <span className="formError">{fieldErrors.gender}</span>}
              </p>
            </fieldset>
            <fieldset className='fieldset-div'>
              <div className='row'>
                <div className='row-terms'>
                  <input className='check-terms' type="checkbox" id="agree" name='termsConditionsAccepted' onChange={() => setAgree(!agree)} required/>
                  <div className='link-terms'>Acepto los términos, condiciones y las políticas de privacidad</div>
                </div>
                <a onClick={() => setShowTermsConditions(true)} className='link-terms2'> Mostrar términos y condiciones </a>
                <div>
                  {showTermsConditions &&
                    <Dialog open={showTermsConditions} scroll='paper' maxWidth='lg'>
                      <DialogActions>
                        <button className='closeForm' onClick={() => setShowTermsConditions(false)}><CloseIcon /></button>
                      </DialogActions>
                      <DialogTitle id="scroll-dialog-title">
                      <h4> <strong>TÉRMINOS Y CONDICIONES Y AVISO DE PRIVACIDAD</strong></h4>
                      </DialogTitle>
                          <DialogContent>
                            <DialogContentText
                              id="scroll-dialog-description"
                              tabIndex={-1}
                            >
                              {termsConditions()}
                            </DialogContentText>
                          </DialogContent>
                    </Dialog>
                  }
                </div>
              </div>
            </fieldset>
          </div>
        <div className='register-buttons'>
          <button id='create-account' className='register-form-button' type="submit">Crear Cuenta</button>
          <button type='button' className='register-form-button' onClick={props.closeRegisterOpenSignIn}>¿Tienes Cuenta? ¡Ingresa!</button>
        </div>
      </>
    </form>
  );
}

RegisterForm.propTypes = {
  navigate: PropTypes.func,
  closeRegisterForm: PropTypes.func,
  closeRegisterOpenSignIn: PropTypes.func,
  closeRegisterOpenService: PropTypes.func
};

export default RegisterForm;
